'use client';

import { FC, useEffect, useRef, useState } from 'react';
import { analytics } from '../lib/firebase/app';
import { logEvent } from 'firebase/analytics';
import { Box, Skeleton } from '@mui/material';

interface EmbeddedTourProps {
    modelId: string; // Matterport ID or YouVisit URL
    width?: string;
    height?: string;
    tourType?: 'matterport' | 'youvisit'; // Type of tour
}

const EmbeddedTour: FC<EmbeddedTourProps> = ({
    modelId,
    width = '100%',
    height = '480px',
    tourType = 'matterport', // Default to matterport for backward compatibility
}) => {
    // For Matterport, clean the model ID if it contains the full URL
    const cleanModelId = tourType === 'matterport'
        ? modelId.replace('https://my.matterport.com/show/?m=', '')
        : modelId;

    const iframeRef = useRef<HTMLIFrameElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [isLoaded, setIsLoaded] = useState(false);
    const [isVisible, setIsVisible] = useState(false);

    // Enhanced IntersectionObserver implementation
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        console.log('Tour viewer is now visible');
                        setIsVisible(true);
                        observer.disconnect();
                    }
                });
            },
            {
                root: null,
                rootMargin: '50px 0px', // Reduced margin to ensure more accurate visibility detection
                threshold: 0
            }
        );

        if (containerRef.current) {
            observer.observe(containerRef.current);
            console.log('Observer attached to tour container');
        }

        return () => {
            observer.disconnect();
        };
    }, []);

    useEffect(() => {
        // Log when the component mounts (viewer is shown)
        if (analytics && isVisible) {
            logEvent(analytics, `${tourType}_viewer_shown`, {
                model_id: cleanModelId,
                tour_type: tourType,
                timestamp: new Date().toISOString(),
            });

            // Set a timeout to consider the iframe "loaded" after 2 seconds
            // This helps with perceived performance
            const timeout = setTimeout(() => {
                setIsLoaded(true);
            }, 2000);

            return () => clearTimeout(timeout);
        }
    }, [cleanModelId, isVisible, tourType]);

    // Prevent touch events from causing page scroll when interacting with the 3D tour
    useEffect(() => {
        const preventTouchScroll = (e: TouchEvent) => {
            e.preventDefault();
            // Still allow scrolling within the iframe
            if (iframeRef.current) {
                iframeRef.current.focus();
            }
        };

        const container = containerRef.current;
        if (container) {
            container.addEventListener('touchmove', preventTouchScroll, { passive: false });
            container.addEventListener('touchstart', () => {
                if (iframeRef.current) {
                    iframeRef.current.focus();
                }
            }, { passive: true });
        }

        return () => {
            if (container) {
                container.removeEventListener('touchmove', preventTouchScroll);
                container.removeEventListener('touchstart', () => { });
            }
        };
    }, [isLoaded]);

    const handleIframeLoad = () => {
        setIsLoaded(true);
        // Log when iframe is actually loaded
        if (analytics) {
            logEvent(analytics, `${tourType}_viewer_loaded`, {
                model_id: cleanModelId,
                tour_type: tourType,
                timestamp: new Date().toISOString(),
            });
        }
    };

    const handleIframeClick = () => {
        // Log when user interacts with the viewer
        if (analytics) {
            logEvent(analytics, `${tourType}_viewer_interaction`, {
                model_id: cleanModelId,
                tour_type: tourType,
                timestamp: new Date().toISOString(),
            });
        }
    };

    // Get the appropriate iframe source URL based on tour type
    const getIframeSrc = () => {
        if (tourType === 'matterport') {
            return `https://my.matterport.com/show/?m=${cleanModelId}&help=0&play=1&qs=1`;
        } else if (tourType === 'youvisit') {
            // YouVisit URLs should be used directly - they already contain all needed parameters
            return cleanModelId;
        }
        return '';
    };

    // Prevent default touch behavior
    const handleTouchStart = (e: React.TouchEvent) => {
        e.stopPropagation();
    };

    const handleTouchMove = (e: React.TouchEvent) => {
        e.stopPropagation();
        e.preventDefault();
    };

    // Update the return statement for the loading placeholder
    if (!isVisible) {
        return (
            <Box
                ref={containerRef}
                className="w-full bg-white rounded-lg shadow-md p-0 relative min-h-[480px]"
                sx={{ height, width, overflow: 'hidden' }}
            >
                <Skeleton
                    variant="rectangular"
                    width="100%"
                    height="100%"
                    animation="wave"
                    className="rounded-lg"
                />
                <div className="absolute inset-0 flex items-center justify-center text-gray-500">
                    <div className="text-center">
                        <div className="animate-pulse">Loading 3D Tour...</div>
                        <div className="text-sm text-gray-400 mt-2">Please scroll into view</div>
                    </div>
                </div>
            </Box>
        );
    }

    // For YouVisit, we need a special container to hide the top portion
    if (tourType === 'youvisit') {
        return (
            <div
                ref={containerRef}
                className="w-full bg-white rounded-lg shadow-md p-0 relative overflow-hidden"
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
            >
                {!isLoaded && (
                    <Box
                        className="absolute inset-0 z-10 flex items-center justify-center"
                        sx={{ backgroundColor: 'rgba(255,255,255,0.7)' }}
                    >
                        <Skeleton
                            variant="rectangular"
                            width="100%"
                            height="100%"
                            animation="wave"
                            className="rounded-lg"
                        />
                        <div className="absolute inset-0 flex items-center justify-center text-gray-500">
                            Loading 3D Tour...
                        </div>
                    </Box>
                )}
                <div
                    className="relative overflow-hidden"
                    style={{
                        height,
                        width
                    }}
                >
                    <iframe
                        ref={iframeRef}
                        src={getIframeSrc()}
                        style={{
                            width: '100%',
                            height: 'calc(125% + 50px)', // Make iframe taller
                            marginTop: '-20%', // Hide top portion
                            border: 'none',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            opacity: isLoaded ? 1 : 0.3,
                            transition: 'opacity 0.5s ease-in-out'
                        }}
                        frameBorder="0"
                        allowFullScreen
                        className="rounded-lg shadow-lg"
                        title="YouVisit 3D Viewer"
                        onClick={handleIframeClick}
                        onLoad={handleIframeLoad}
                        loading="lazy"
                        onMouseEnter={() => {
                            if (analytics) {
                                logEvent(analytics, `${tourType}_viewer_hover`, {
                                    model_id: cleanModelId,
                                    tour_type: tourType,
                                    timestamp: new Date().toISOString(),
                                });
                            }
                        }}
                        sandbox="allow-same-origin allow-scripts allow-forms"
                        referrerPolicy="no-referrer"
                        onTouchStart={(e) => {
                            e.preventDefault();
                            handleIframeClick();
                        }}
                    />
                </div>
            </div>
        );
    }

    // Return standard view for Matterport
    return (
        <div
            ref={containerRef}
            className="w-full bg-white rounded-lg shadow-md p-0 relative"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
        >
            {!isLoaded && (
                <Box
                    className="absolute inset-0 z-10 flex items-center justify-center"
                    sx={{ backgroundColor: 'rgba(255,255,255,0.7)' }}
                >
                    <Skeleton
                        variant="rectangular"
                        width="100%"
                        height="100%"
                        animation="wave"
                        className="rounded-lg"
                    />
                    <div className="absolute inset-0 flex items-center justify-center text-gray-500">
                        Loading 3D Tour...
                    </div>
                </Box>
            )}
            <iframe
                ref={iframeRef}
                src={getIframeSrc()}
                width={width}
                height={height}
                frameBorder="0"
                allowFullScreen
                className="rounded-lg shadow-lg p-0"
                title="Matterport 3D Viewer"
                onClick={handleIframeClick}
                onLoad={handleIframeLoad}
                loading="lazy"
                onMouseEnter={() => {
                    if (analytics) {
                        logEvent(analytics, `${tourType}_viewer_hover`, {
                            model_id: cleanModelId,
                            tour_type: tourType,
                            timestamp: new Date().toISOString(),
                        });
                    }
                }}
                sandbox="allow-same-origin allow-scripts allow-forms"
                referrerPolicy="no-referrer"
                onTouchStart={(e) => {
                    e.preventDefault();
                    handleIframeClick();
                }}
                style={{ opacity: isLoaded ? 1 : 0.3, transition: 'opacity 0.5s ease-in-out' }}
            />
        </div>
    );
};

// For backward compatibility, export as both EmbeddedTour and EmbeddedMatterport
export default EmbeddedTour;
export { EmbeddedTour as EmbeddedMatterport };
